import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, Plus, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { inputClass } from "../components/FormField";
import { exportAuditorAuditReportsCsv } from "../api/auditorApi";
import { getStoredAccountType } from "../auth";
import { useAuditReports } from "../hooks/useAuditReports";
import { cn } from "@/shared/lib/cn";

function formatAmount(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function formatDate(value: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

const statusBadgeClass = (status: string) => {
  if (status === "Approved") return "bg-accent/15 text-accent";
  if (status === "Rejected") return "bg-danger-soft text-danger";
  return "bg-card-muted text-muted-foreground";
};

export function AuditReportsPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.auditReports.list;
  const { showToast } = usePageActions();
  const [search, setSearch] = useState("");
  const { reports, loading, error, reload } = useAuditReports();

  const filtered = useMemo(() => {
    if (!search) return reports;
    const haystack = search.toLowerCase();
    return reports.filter((report) =>
      `${report.id} ${report.invoice_number} ${report.hospital_name} ${report.company_name} ${report.auditor_name} ${report.status_label}`
        .toLowerCase()
        .includes(haystack),
    );
  }, [reports, search]);

  const handleExport = async () => {
    try {
      await exportAuditorAuditReportsCsv({ q: search || undefined });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "Export impossible.",
        variant: "danger",
      });
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Breadcrumbs
          homeHref={getDashboardHomeHref()}
          items={[{ label: labels.title }]}
        />
        <Link
          to="/dashboard/audit-reports/submit"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          {labels.submitButton}
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-danger/20 bg-danger-soft px-4 py-3">
          <p className="text-sm text-danger">{error}</p>
        </div>
      )}

      <div className="card-shadow rounded-2xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h1 className="text-lg font-semibold text-foreground">{labels.title}</h1>
            <p className="text-sm text-muted-foreground">{labels.subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={labels.searchPlaceholder}
              className={cn(inputClass, "min-w-[220px]")}
            />
            <button
              type="button"
              onClick={() => void reload()}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <RotateCcw size={16} />
              {labels.reset}
            </button>
            <button
              type="button"
              onClick={() => void handleExport()}
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-foreground transition-colors hover:bg-accent/90"
            >
              <Download size={16} />
              {labels.exportCsv}
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-card-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-5 py-3">{labels.columns.id}</th>
                <th className="px-5 py-3">{labels.columns.auditDate}</th>
                <th className="px-5 py-3">{labels.columns.invoice}</th>
                <th className="px-5 py-3">{labels.columns.hospital}</th>
                <th className="px-5 py-3">{labels.columns.company}</th>
                <th className="px-5 py-3">{labels.columns.auditor}</th>
                <th className="px-5 py-3">{labels.columns.originalAmount}</th>
                <th className="px-5 py-3">{labels.columns.basketTotal}</th>
                <th className="px-5 py-3">{labels.columns.status}</th>
                <th className="px-5 py-3">{labels.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-5 py-10 text-center text-muted-foreground">
                    {labels.loading}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-5 py-10 text-center text-muted-foreground">
                    {labels.empty}
                  </td>
                </tr>
              ) : (
                filtered.map((report) => (
                  <tr key={report.id} className="border-t border-border/70 hover:bg-card-muted/40">
                    <td className="px-5 py-3 font-medium">{report.id}</td>
                    <td className="px-5 py-3">{formatDate(report.audit_date)}</td>
                    <td className="px-5 py-3">
                      <Link
                        to={`/dashboard/invoices/${report.invoice_id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        #{report.invoice_number}
                      </Link>
                    </td>
                    <td className="px-5 py-3">{report.hospital_name}</td>
                    <td className="px-5 py-3">{report.company_name}</td>
                    <td className="px-5 py-3">{report.auditor_name}</td>
                    <td className="px-5 py-3">{formatAmount(report.original_amount)}</td>
                    <td className="px-5 py-3">{formatAmount(report.basket_total)}</td>
                    <td className="px-5 py-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
                          statusBadgeClass(report.status),
                        )}
                      >
                        {report.status_label}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Link
                        to={`/dashboard/audit-reports/${report.id}?source=${report.source}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
                        title={labels.view}
                      >
                        <Eye size={14} />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
